import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  EmptyState,
  Field,
  IconBell,
  IconCheck,
  IconCheckCircle,
  IconExternal,
  IconInbox,
  NumberInput,
  Pagination,
  Select,
  Skeleton,
  Switch,
  Tabs,
  cn,
  useToast,
} from '@/design-system';
import { notificationsApi, queryKeys, toUserMessage } from '@/lib/api';
import type { InAppNotification, NotificationPreferences } from '@/lib/api/types';
import { formatRegulatedDateTime, formatRelative } from '@/lib/format/datetime';
import { useOrganizationTimeZone } from '@/providers/auth-provider';
import { PageBody, PageHeader } from '@/shell/app-shell';

/**
 * Caixa de notificações e preferências de resumo.
 *
 * A entrega de notificação é auditável (plano §4.2): "o aprovador não foi
 * notificado" é defesa comum em inspeção e precisa ser refutável com evidência.
 * Por isso a caixa mostra data e hora de criação no fuso da organização, e o
 * evento de leitura é registrado no servidor ao marcar como lida.
 */
export function NotificationsPage() {
  const [tab, setTab] = useState('caixa');

  return (
    <>
      <PageHeader
        title="Notificações"
        description="Avisos de aprovação pendente, vencimento de documento e solicitação de ciência. A entrega é registrada e pode ser comprovada em auditoria."
      />
      <PageBody>
        <Tabs
          ariaLabel="Seções de notificações"
          value={tab}
          onValueChange={setTab}
          tabs={[
            { value: 'caixa', label: 'Caixa de entrada', content: <InboxSection /> },
            { value: 'preferencias', label: 'Preferências', content: <PreferencesSection /> },
          ]}
        />
      </PageBody>
    </>
  );
}

function InboxSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const timeZone = useOrganizationTimeZone();
  const [page, setPage] = useState(1);
  const [onlyUnread, setOnlyUnread] = useState(false);

  const inboxQuery = useQuery({
    queryKey: queryKeys.notifications.inbox(page, onlyUnread),
    queryFn: () => notificationsApi.inbox({ page, pageSize: 25, onlyUnread }),
    placeholderData: keepPreviousData,
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
  };

  const markRead = useMutation({
    mutationFn: (ids: readonly string[]) => notificationsApi.markRead(ids),
    onSuccess: invalidate,
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: async () => {
      await invalidate();
      toast({ tone: 'success', title: 'Todas as notificações foram marcadas como lidas' });
    },
    onError: (error) =>
      toast({ tone: 'error', title: 'Falha ao marcar', description: toUserMessage(error) }),
  });

  const items = inboxQuery.data?.items ?? [];
  const unreadCount = inboxQuery.data?.unreadCount ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Checkbox
            checked={onlyUnread}
            onCheckedChange={(checked) => {
              setOnlyUnread(checked);
              setPage(1);
            }}
            label="Apenas não lidas"
          />
          {unreadCount > 0 ? (
            <Badge tone="brand" srPrefix="Total:">
              {unreadCount} não {unreadCount === 1 ? 'lida' : 'lidas'}
            </Badge>
          ) : null}
        </div>

        {unreadCount > 0 ? (
          <Button
            variant="secondary"
            leadingIcon={<IconCheck size={15} />}
            loading={markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            Marcar todas como lidas
          </Button>
        ) : null}
      </div>

      {inboxQuery.isPending ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-20 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<IconCheckCircle size={22} />}
            title={onlyUnread ? 'Nenhuma notificação não lida' : 'Caixa de entrada vazia'}
            description={
              onlyUnread
                ? 'Você já leu todas as notificações. Desmarque o filtro para ver o histórico completo.'
                : 'Você ainda não recebeu notificações. Elas aparecem aqui quando houver aprovação pendente, documento vencendo ou solicitação de ciência.'
            }
          />
        </Card>
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {items.map((item) => (
              <NotificationRow
                key={item.id}
                notification={item}
                timeZone={timeZone}
                onMarkRead={() => markRead.mutate([item.id])}
              />
            ))}
          </ul>

          {inboxQuery.data && inboxQuery.data.total > 0 ? (
            <Card padding="none">
              <Pagination
                page={inboxQuery.data.page}
                pageSize={inboxQuery.data.pageSize}
                total={inboxQuery.data.total}
                itemNoun="notificações"
                onPageChange={setPage}
              />
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}

function NotificationRow({
  notification,
  timeZone,
  onMarkRead,
}: {
  readonly notification: InAppNotification;
  readonly timeZone: string;
  readonly onMarkRead: () => void;
}) {
  const unread = notification.readAt === null;

  return (
    <li>
      <article
        className={cn(
          'flex items-start gap-3 rounded-lg border bg-surface p-4',
          unread ? 'border-l-4 border-l-brand border-border' : 'border-border',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
            unread ? 'bg-brand-subtle text-brand' : 'bg-surface-sunken text-content-subtle',
          )}
        >
          <IconBell size={16} />
        </span>

        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-content">
            {/* `subject` e `queuedAt` são os nomes que o serviço devolve
                (`InboxItemView`). Lidos como `title`/`createdAt`, saíam
                `undefined`: título em branco e data inválida. */}
            {notification.subject}
            {/* O estado "não lida" também é TEXTO, não só a barra colorida. */}
            {unread ? <span className="qc-sr-only"> — não lida</span> : null}
          </h3>
          <p className="mt-0.5 text-sm text-content-muted">{notification.body}</p>
          <p className="mt-1.5 text-xs text-content-subtle qc-tabular">
            <time dateTime={notification.queuedAt}>
              {formatRegulatedDateTime(notification.queuedAt, timeZone)}
            </time>
            <span aria-hidden="true"> · </span>
            {formatRelative(notification.queuedAt)}
          </p>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {notification.actionUrl ? (
            <Button
              asChild
              variant="ghost"
              size="sm"
              trailingIcon={<IconExternal size={13} />}
            >
              <a href={notification.actionUrl}>Abrir</a>
            </Button>
          ) : null}
          {unread ? (
            <Button variant="ghost" size="sm" onClick={onMarkRead}>
              Marcar como lida
            </Button>
          ) : (
            <Badge tone="neutral" size="sm">
              Lida
            </Badge>
          )}
        </div>
      </article>
    </li>
  );
}

function PreferencesSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const preferencesQuery = useQuery({
    queryKey: queryKeys.notifications.preferences(),
    queryFn: () => notificationsApi.preferences(),
  });

  const [draft, setDraft] = useState<NotificationPreferences | null>(null);
  const current = draft ?? preferencesQuery.data ?? null;

  const save = useMutation({
    mutationFn: (values: NotificationPreferences) => notificationsApi.updatePreferences(values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.notifications.preferences() });
      setDraft(null);
      toast({ tone: 'success', title: 'Preferências salvas' });
    },
    onError: (error) =>
      toast({ tone: 'error', title: 'Falha ao salvar', description: toUserMessage(error) }),
  });

  if (preferencesQuery.isPending || !current) {
    return (
      <Card>
        <Skeleton className="h-5 w-48" />
        <Skeleton className="mt-4 h-40 w-full" />
      </Card>
    );
  }

  return (
    <Card className="max-w-2xl">
      <CardHeader
        headingLevel={3}
        title="Como você quer ser avisado"
        description="Estas preferências valem apenas para você e não afetam o registro de entrega usado em auditoria."
      />

      <div className="mt-5 flex flex-col gap-5">
        <Switch
          checked={current.inAppEnabled}
          onCheckedChange={(checked) => setDraft({ ...current, inAppEnabled: checked })}
          stateLabels={{ on: 'Ativas', off: 'Desativadas' }}
          label="Notificações no sistema"
          description="Aparecem na caixa de entrada e no indicador da barra superior."
        />

        <Switch
          checked={current.emailEnabled}
          onCheckedChange={(checked) => setDraft({ ...current, emailEnabled: checked })}
          stateLabels={{ on: 'Ativas', off: 'Desativadas' }}
          label="Notificações por e-mail"
          description="Exige que a organização tenha servidor de e-mail configurado."
        />

        <div className="rounded-md border border-border bg-surface-sunken p-4">
          <h4 className="text-sm font-semibold text-content">Resumo periódico</h4>
          <p className="mt-0.5 text-xs text-content-muted">
            Em vez de um e-mail por evento, agrupa as pendências num único envio.
          </p>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Frequência">
              <Select
                value={current.digestFrequency}
                onValueChange={(value) =>
                  setDraft({
                    ...current,
                    digestFrequency: value as NotificationPreferences['digestFrequency'],
                  })
                }
                options={[
                  {
                    // O serviço aceita IMMEDIATE | DAILY | WEEKLY. `NONE` não
                    // existe: escolhê-lo devolvia COMMON.VALIDATION_FAILED.
                    value: 'IMMEDIATE',
                    label: 'Sem resumo',
                    description: 'Um e-mail por evento, na hora.',
                  },
                  { value: 'DAILY', label: 'Diário' },
                  { value: 'WEEKLY', label: 'Semanal' },
                ]}
              />
            </Field>

            {current.digestFrequency !== 'IMMEDIATE' ? (
              <Field
                label="Horário de envio"
                description="Hora no fuso da organização (0 a 23)."
              >
                <NumberInput
                  min={0}
                  max={23}
                  value={current.digestHour}
                  onChange={(event) =>
                    setDraft({ ...current, digestHour: Number(event.target.value) })
                  }
                  className="w-24"
                />
              </Field>
            ) : null}
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-md border border-info bg-info-subtle px-3 py-2.5 text-sm text-info-content">
          <IconInbox size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
          <span>
            Avisos de <strong className="font-semibold">bloqueio de conta</strong> e de{' '}
            <strong className="font-semibold">ciência em atraso</strong> são sempre enviados, mesmo
            com as notificações desligadas: são controles regulados e não podem ser silenciados.
          </span>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" disabled={!draft} onClick={() => setDraft(null)}>
            Descartar alterações
          </Button>
          <Button
            variant="primary"
            disabled={!draft}
            loading={save.isPending}
            onClick={() => draft && save.mutate(draft)}
          >
            Salvar preferências
          </Button>
        </div>
      </div>
    </Card>
  );
}
